import { z } from 'zod';

/**
 * Express Multer File interface (defined inline to avoid dependency on @types/express)
 */
export interface MulterFile {
  fieldname: string;
  originalname: string;
  encoding: string;
  mimetype: string;
  size: number;
  buffer: Buffer;
  destination?: string;
  filename?: string;
  path?: string;
}

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

const ALLOWED_MIME_TYPES = new Set([
  // Documents
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',

  // Images
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/svg+xml',

  // Text
  'text/plain',
  'text/csv',

  // Archives (be cautious - can contain malware)
  'application/zip',
  'application/x-zip-compressed',
]);

/**
 * File Upload Schema (Express Multer)
 *
 * Validates file uploads to prevent malware, oversized files, and malicious content.
 * Replaces unsafe `z.any()` with actual runtime validation.
 *
 * Security features:
 * - Enforces 50MB file size limit
 * - Validates mime type against whitelist
 * - Ensures required fields are present
 * - Prevents buffer overflow attacks
 *
 * @example
 * ```typescript
 * const schema = z.object({
 *   file: FileUploadSchema,
 * });
 *
 * // Will reject files > 50MB or with disallowed mime types
 * schema.parse({ file: uploadedFile });
 * ```
 */
export const FileUploadSchema = z.custom<MulterFile>(
  (val) => {
    if (typeof val !== 'object' || val === null) {
      return false;
    }

    // biome-ignore lint/suspicious/noExplicitAny: Validating unknown file upload object shape
    const file = val as any;

    if (
      typeof file.originalname !== 'string' ||
      typeof file.mimetype !== 'string' ||
      typeof file.size !== 'number' ||
      !Buffer.isBuffer(file.buffer)
    ) {
      return false;
    }

    if (file.size > MAX_FILE_SIZE) return false;
    return ALLOWED_MIME_TYPES.has(file.mimetype);
  },
  {
    message:
      'Invalid file upload. Must be a valid file object with allowed mime type (PDF, Word, Excel, images, etc.) and size < 50MB',
  },
);

/**
 * TypeScript type for Express Multer file uploads
 */
export type FileUpload = z.infer<typeof FileUploadSchema>;

/**
 * File Upload Schema (Fastify)
 *
 * Alternative validation for Fastify file uploads which use a different structure.
 *
 * Security features:
 * - Same 50MB size limit as Express version
 * - Validates all required fields
 * - Prevents malformed uploads
 *
 * @example
 * ```typescript
 * const schema = z.object({
 *   file: FastifyFileSchema,
 * });
 *
 * schema.parse({ file: fastifyFile });
 * ```
 */
export const FastifyFileSchema = z.custom<{
  filename: string;
  mimetype: string;
  encoding: string;
  data: Buffer;
}>(
  (val) => {
    if (typeof val !== 'object' || val === null) {
      return false;
    }

    // biome-ignore lint/suspicious/noExplicitAny: Validating unknown file upload object shape
    const file = val as any;

    if (
      typeof file.filename !== 'string' ||
      typeof file.mimetype !== 'string' ||
      typeof file.encoding !== 'string' ||
      !Buffer.isBuffer(file.data)
    ) {
      return false;
    }

    if (file.data.length > MAX_FILE_SIZE) return false;
    return ALLOWED_MIME_TYPES.has(file.mimetype);
  },
  {
    message:
      'Invalid Fastify file object. Must have filename, mimetype (from whitelist), encoding, and data (Buffer < 50MB)',
  },
);

/**
 * TypeScript type for Fastify file uploads
 */
export type FastifyFile = z.infer<typeof FastifyFileSchema>;
